import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { cn } from '@/lib/utils/cn';
import { Download, Eye, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useDownloadReports, useDownloadWebhookReports, useBulkReportCampaigns } from '../api';
import type { ReportDownloadRow } from '../types';

const BULK_TYPES = ['bulk', 'dtmf_bulk', 'connected_bulk'];

const REPORT_TYPE_LABELS: Record<string, string> = {
  full: 'Full',
  connected_calls: 'Connected Calls',
  dtmf: 'DTMF',
  sms: 'SMS',
  bulk: 'Bulk',
  dtmf_bulk: 'DTMF Bulk',
  connected_bulk: 'Connected Bulk',
  continuous: 'Continuous',
  webhook: 'Webhook',
  api_webhook: 'API Webhook',
};

const STATUS_LABEL: Record<string, { label: string; tone: 'neutral' | 'pending' | 'ready' }> = {
  '0': { label: 'Initiated', tone: 'pending' },
  '1': { label: 'Processing', tone: 'pending' },
  '2': { label: 'Ready', tone: 'ready' },
};

/** Estimated processing time per report type (in seconds). */
const EST_SECONDS: Record<string, number> = {
  CDR: 60,
  SUMMARY: 60,
  DEFAULT: 60,
};

/** Parse reqDate string → Date. Handles "YYYY-MM-DD HH:mm:ss" IST format. */
function parseReqDate(raw?: string): Date | null {
  if (!raw) return null;
  const d = new Date(raw.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Returns remaining seconds (0 if elapsed). */
function calcRemaining(reqDate?: string, reportType?: string): number {
  const start = parseReqDate(reqDate);
  if (!start) return 0;
  const estKey = (reportType ?? '').toUpperCase();
  const totalSec = EST_SECONDS[estKey] ?? EST_SECONDS['DEFAULT'] ?? 180;
  const elapsed = (Date.now() - start.getTime()) / 1000;
  return Math.max(0, Math.ceil(totalSec - elapsed));
}

/** Format seconds → "2m 30s" */
function fmtTime(sec: number): string {
  if (sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/** Hook: ticks every second while any row is pending. */
function useTick(hasPending: boolean) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!hasPending) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [hasPending]);
  return tick;
}

function statusBadge(status: string | undefined) {
  const meta = STATUS_LABEL[String(status ?? '')] ?? { label: '\u2014', tone: 'neutral' as const };
  return (
    <Badge
      variant="outline"
      className={cn(
        'font-normal',
        meta.tone === 'pending' && 'border-amber-200 bg-amber-50 text-amber-900',
        meta.tone === 'ready' && 'border-emerald-200 bg-emerald-50 text-emerald-900',
      )}
    >
      {meta.label}
    </Badge>
  );
}

function BulkCampaignsModal({
  reportId,
  reportType,
  highlight,
  onClose,
}: {
  reportId: number | string;
  reportType?: string;
  highlight?: string;
  onClose: () => void;
}) {
  const query = useBulkReportCampaigns(reportId);
  const campaigns = query.data ?? [];
  const typeLabel = REPORT_TYPE_LABELS[reportType ?? ''] ?? reportType ?? '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 max-h-[70vh] w-full max-w-lg overflow-hidden rounded-lg border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">
            {typeLabel} Report — Campaigns ({campaigns.length})
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg leading-none">
            ✕
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {query.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading...</p>
          ) : campaigns.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No campaigns found.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Campaign ID</th>
                  <th className="px-4 py-2 text-left font-medium">Campaign Name</th>
                </tr>
              </thead>
              <tbody>
                {[...campaigns]
                  .sort((a, b) => {
                    if (!highlight) return 0;
                    const aMatch = String(a.campaignId).includes(highlight) || a.campaignName.toLowerCase().includes(highlight.toLowerCase());
                    const bMatch = String(b.campaignId).includes(highlight) || b.campaignName.toLowerCase().includes(highlight.toLowerCase());
                    if (aMatch && !bMatch) return -1;
                    if (!aMatch && bMatch) return 1;
                    return 0;
                  })
                  .map((c) => {
                    const isMatch = highlight
                      ? String(c.campaignId).includes(highlight) || c.campaignName.toLowerCase().includes(highlight.toLowerCase())
                      : false;
                    return (
                      <tr key={c.campaignId} className={cn('border-t', isMatch && 'bg-muted font-medium ring-2 ring-inset ring-primary/40 rounded')}>
                        <td className={cn('px-4 py-2 font-mono text-xs', isMatch ? 'text-foreground' : 'text-muted-foreground')}>{c.campaignId}</td>
                        <td className="px-4 py-2">{c.campaignName}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export function DownloadReportPage() {
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const canView = usePermission('Manage Campaign.Download Report', BASE_PERMISSION.VIEW);
  const query = useDownloadReports();
  const webhookQuery = useDownloadWebhookReports();
  const rows = useMemo(() => {
    const merged = [...(query.data ?? []), ...(webhookQuery.data ?? [])];
    return merged.sort((a, b) => {
      const ta = parseReqDate(a.reqDate)?.getTime() ?? 0;
      const tb = parseReqDate(b.reqDate)?.getTime() ?? 0;
      return tb - ta; // newest first
    });
  }, [query.data, webhookQuery.data]);
  const hasPending = rows.some((r) => String(r.status) !== '2');
  useTick(hasPending);

  const [bulkModal, setBulkModal] = useState<{ reportId: number | string; reportType?: string; highlight?: string } | null>(null);

    // Merge campaignIds into a searchable field so global filter matches bulk rows
  const enrichedRows = useMemo(() =>
    rows.map((r) => {
      const isBulk = BULK_TYPES.includes(r.reportType ?? '');
      return {
        ...r,
        // For bulk rows, put campaignIds into campaignId so global search matches
        campaignId: isBulk ? (r.campaignIds ?? '') : r.campaignId,
        // Also put campaignIds into campaignName so name search also works
        campaignName: isBulk ? [r.campaignName, r.campaignIds].filter(Boolean).join(' ') : r.campaignName,
      };
    }),
  [rows]);

  const columns = useMemo<ColumnDef<ReportDownloadRow>[]>(() => {
    const cols: ColumnDef<ReportDownloadRow>[] = [

      {
        accessorKey: 'campaignId',
        header: 'Campaign ID',
        size: 130,
        cell: ({ row }) => {
          const r = row.original;
          const isBulk = BULK_TYPES.includes(r.reportType ?? '');
          return (
            <span className="font-mono text-xs text-muted-foreground">
              {isBulk ? '\u2014' : (r.campaignId ?? '\u2014')}
            </span>
          );
        },
      },
      {
        accessorKey: 'campaignName',
        header: 'Campaign',
        size: 240,
        cell: ({ row }) => {
          const r = row.original;
          const isBulk = BULK_TYPES.includes(r.reportType ?? '');
          if (isBulk) {
            return (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const input = document.querySelector<HTMLInputElement>('input[placeholder="Search reports..."]');
                  setBulkModal({ reportId: r.reportId ?? r.campaignId ?? '', reportType: r.reportType, highlight: input?.value ?? '' });
                }}
              >
                <Eye className="h-4 w-4" />
                View
              </Button>
            );
          }
          return <span>{r.campaignName ?? '\u2014'}</span>;
        },
      },

    ];
    if (isSubOrSuper) {
      cols.push({ accessorKey: 'username', header: 'Username', size: 160 });
    }

    cols.push(
      { accessorKey: 'reqDate', header: 'Requested', size: 180 },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ row }) => statusBadge(row.original.status as string),
      },
            {
        id: 'estTime',
        header: 'Est. Time',
        size: 120,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          if (String(r.status) === '2') {
            return <span className="text-xs text-emerald-600 font-medium">Ready</span>;
          }
          const rem = calcRemaining(r.reqDate, r.reportType);
          if (rem <= 0) {
            return (
              <span className="flex items-center gap-1 text-xs text-amber-600">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Almost done
              </span>
            );
          }
          return (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              ~{fmtTime(rem)}
            </span>
          );
        },
      },
      {
        accessorKey: 'reportType',
        header: 'Type',
        size: 130,
        cell: ({ row }) => {
          const raw = row.original.reportType ?? '';
          return <span>{REPORT_TYPE_LABELS[raw] ?? raw}</span>;
        },
      },
      {
        id: 'download',
        header: 'Settings',
        size: 140,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          if (String(r.status) !== '2' || !r.reportUrl) {
            return <span className="text-xs text-muted-foreground">Not ready</span>;
          }
          if (r.reportUrl === 'no_data') {
            return <span className="text-xs text-amber-600 font-medium">No Data</span>;
          }
          return (
            <Button asChild size="sm" variant="outline">
              <a href={r.reportUrl} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4" />
                Download
              </a>
            </Button>
          );
        },
      },
    );

    return cols;
  }, [isSubOrSuper]);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Downloads"
        description="Generated campaign reports \u2014 click a row to download once it's ready."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => { query.refetch(); webhookQuery.refetch(); }}
            disabled={query.isFetching}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', query.isFetching && 'animate-spin')} />
          </Button>
        }
      />

      <DataTable<ReportDownloadRow, unknown>
        tableId="reports:download:v1"
        columns={columns}
        data={enrichedRows}
        isLoading={query.isLoading}
        searchPlaceholder="Search reports..."
        emptyTitle="No reports yet"
        emptyDescription="Reports you generate from the Summary screens will appear here."
        getRowId={(row, i) => `${row.campaignId ?? 'noid'}:${row.reqDate ?? i}`}
        initialColumnPinning={{ right: ['download'] }}
        exportFilename="reports.csv"
        pageSize={10}
      />
      {bulkModal && (
        <BulkCampaignsModal
          reportId={bulkModal.reportId}
          reportType={bulkModal.reportType}
          highlight={bulkModal.highlight}
          onClose={() => setBulkModal(null)}
        />
      )}
    </FillPage>
  );
}